import React from "react";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import classes from "./MisaBrandSelector.module.scss";
import { MisaBrand } from "../../pages/misa-top/MisaTypes";
import downArrow from "../../static/images/misa/down-arrow.svg";
import flatArrow from "../../static/images/misa/flat-arrow.svg";
import upArrow from "../../static/images/misa/up-arrow.svg";
import sadIcon from "../../static/images/misa/sad.png";
import happyIcon from "../../static/images/misa/happy.png";
import verySadIcon from "../../static/images/misa/verysad.png";
import veryHappyIcon from "../../static/images/misa/veryhappy.png";
import sampleStockImage from "../../static/images/misa/sample-stock.png";

type Props = {
  // 表示対象の銘柄一覧。選択モードでは全銘柄、通常表示では応援中の銘柄だけが渡される。
  brands: MisaBrand[];
  // 子どものポイント保有主体が選ばれている場合、背景色を子ども用に切り替える。
  isChildSelected: boolean;
  // 銘柄カード一覧を表示するかどうか。空状態の表示制御は親コンポーネントで行う。
  showCards: boolean;
  // 銘柄選択モード中に戻るボタンと価格更新案内を表示する。
  showBackButton: boolean;
  // 通常表示で応援中の銘柄がある場合に表示する追加アクションのラベル。
  actionButtonLabel?: string;
  // 銘柄カードを押したとき、対象銘柄を親へ渡して応援モーダルを開く。
  onBrandClick: (brand: MisaBrand) => void;
  // 銘柄選択モードを閉じる。
  onBackClick: () => void;
  // 銘柄選択モードを開く、または追加アクションカードから親の操作を実行する。
  onActionButton: () => void;
};

// 価格変動率に応じて、表示する矢印・天気アイコン・色を決める。
const getTrendBadge = (trendRate = 0) => {
  if (trendRate <= -1) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: downArrow,
      weatherIcon: verySadIcon,
      className: classes.downTrend,
    };
  }

  if (trendRate < 0) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: downArrow,
      weatherIcon: sadIcon,
      className: classes.downTrend,
    };
  }

  if (trendRate === 0) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: flatArrow,
      weatherIcon: null,
      className: classes.neutralTrend,
    };
  }

  if (trendRate < 1) {
    return {
      rateText: `+${trendRate.toFixed(1)}%`,
      arrowIcon: upArrow,
      weatherIcon: happyIcon,
      className: classes.upTrend,
    };
  }

  return {
    rateText: `+${trendRate.toFixed(1)}%`,
    arrowIcon: upArrow,
    weatherIcon: veryHappyIcon,
    className: classes.upTrend,
  };
};

const MisaBrandSelector = ({
  brands,
  isChildSelected,
  showCards,
  showBackButton,
  actionButtonLabel,
  onBrandClick,
  onBackClick,
  onActionButton,
}: Props) => {
  // 1銘柄だけの通常表示では中央寄せにし、追加アクションがあれば横に並べる。
  const isSingleBrand = brands.length === 1;
  const showInlineAddAction = isSingleBrand && Boolean(actionButtonLabel);
  // 保有ポイントバッジが1つでもある場合、カード上部の余白を一覧全体で揃える。
  const hasHoldingBadge = brands.some(
    (brand) => (brand.holdingUnits ?? 0) > 0 || (brand.holdingPoint ?? 0) > 0
  );

  return (
    <section
      className={`${classes.container} ${
        isChildSelected ? classes.childSelectedContainer : ""
      }`}
    >
      {showCards && brands.length > 0 && (
        <>
          {/* 選択モードの上部操作。価格更新時刻も同じタイミングで案内する。 */}
          {showBackButton && (
            <div className={classes.backButton}>
              <SimpleButton
                label="戻る"
                width="140px"
                color="gray"
                isRadius={true}
                onClick={onBackClick}
              />
            </div>
          )}
          {showBackButton && (
            <div className={classes.priceUpdateNotice}>
              平日18時に価格更新
            </div>
          )}
          <div
            className={`${classes.grid} ${
              hasHoldingBadge ? classes.gridWithHoldingBadge : ""
            } ${
              isSingleBrand ? classes.singleBrandGrid : ""
            } ${
              showInlineAddAction ? classes.gridWithInlineAddAction : ""
            }`}
          >
            {brands.map((brand) => {
              // API 値がない場合でもカード表示が崩れないよう、表示用の値をここで補完する。
              const trendBadge = getTrendBadge(brand.trendRate);
              const unitPriceText = `${(brand.unitPrice ?? 0).toLocaleString("ja-JP")}pt`;
              const holdingPoint = brand.holdingPoint ?? 0;
              const hasInvestment =
                (brand.holdingUnits ?? 0) > 0 || holdingPoint > 0;
              const brandImage = brand.imageData ?? sampleStockImage;
              const brandLabel = brand.indexName ?? brand.id;
              return (
                <button
                  key={brand.id}
                  type="button"
                  className={classes.imageCard}
                  onClick={() => onBrandClick(brand)}
                >
                  {/* 応援中の銘柄だけ、現在の保有ポイントをカード上部に表示する。 */}
                  {hasInvestment && (
                    <div className={classes.holdingPointBadge}>
                      <span className={classes.holdingPointValue}>
                        {holdingPoint.toLocaleString("ja-JP")}pt
                      </span>
                    </div>
                  )}
                  <div className={classes.imageCardVisual}>
                    <img src={brandImage} alt={brandLabel} />
                  </div>
                  {/* 値動きの方向と変動率、現在価格をひとまとめに表示する。 */}
                  <div className={`${classes.trendBadge} ${trendBadge.className}`}>
                    {trendBadge.weatherIcon && (
                      <img
                        src={trendBadge.weatherIcon}
                        alt=""
                        className={classes.trendWeatherIcon}
                      />
                    )}
                    <div
                      className={`${classes.trendTextGroup} ${
                        !trendBadge.weatherIcon ? classes.centeredTrendTextGroup : ""
                      }`}
                    >
                      <div
                        className={`${classes.trendRateRow} ${
                          !trendBadge.weatherIcon ? classes.centeredTrendRow : ""
                        }`}
                      >
                        {trendBadge.arrowIcon && (
                          <img src={trendBadge.arrowIcon} alt="" />
                        )}
                        <span>{trendBadge.rateText}</span>
                      </div>
                      <div
                        className={`${classes.trendLabelRow} ${
                          !trendBadge.weatherIcon ? classes.centeredTrendRow : ""
                        }`}
                      >
                        <span>{unitPriceText}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            {/* 1銘柄だけ応援中の通常表示では、空いた枠に「推しを増やす」導線を出す。 */}
            {showInlineAddAction && (
              <button
                type="button"
                className={classes.inlineAddCard}
                onClick={onActionButton}
              >
                <span className={classes.inlineAddIcon}>+</span>
                <span className={classes.inlineAddLabel}>推しを増やす</span>
              </button>
            )}
          </div>
          {/* 複数銘柄の通常表示では、一覧下部に追加アクションを表示する。 */}
          {actionButtonLabel && (
            <div className={classes.actionButton}>
              <SimpleButton
                label={actionButtonLabel}
                width="100%"
                color="orange"
                isRadius={true}
                onClick={onActionButton}
              />
            </div>
          )}
          {/* 選択モードの下部操作。長い一覧でも下までスクロールした後に戻れるようにする。 */}
          {showBackButton && (
            <div className={classes.backButton}>
              <SimpleButton
                label="戻る"
                width="140px"
                color="gray"
                isRadius={true}
                onClick={onBackClick}
              />
            </div>
          )}
        </>
      )}

    </section>
  );
};

export default MisaBrandSelector;
